import { useCallback, useState } from 'react';
import { motion } from 'framer-motion';
import CounterList from './components/common/CounterList';
import SelectableCard from './components/common/SelectableCard';
import SelectableCardGroup from './components/common/SelectableCardGroup';
import DatePreferencePicker from './components/common/DatePreferencePicker';
import SearchInput from './components/common/SearchInput';
import ChipGroup from './components/common/ChipGroup';
import InfoNotice from './components/common/InfoNotice';
import type {
  DateSection,
  FormSection,
  FormValueKey,
  FormalwearFormValues,
  KnownShopSection,
  KnownShopValue,
  SelectSection,
  TextareaSection,
} from './formTypes';

interface FormSectionRendererProps {
  section: FormSection;
  values: FormalwearFormValues;
  setFieldValue: <K extends FormValueKey>(key: K, value: FormalwearFormValues[K]) => void;
}

function SectionTitle({ title, desc }: { title?: string; desc?: string }) {
  if (!title && !desc) return null;

  return (
    <div className="mb-3">
      {title && <p className="text-xs font-semibold text-navy">{title}</p>}
      {desc && <p className="text-[11px] text-navy-light mt-1">{desc}</p>}
    </div>
  );
}

function hasDateFormat(value: string | null) {
  return !!value && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function SelectSectionRenderer({
  section,
  value,
  onChange,
}: {
  section: SelectSection;
  value: string | string[] | null;
  onChange: (value: string | string[]) => void;
}) {
  return (
    <div>
      <SectionTitle title={section.title} desc={section.desc} />
      <SelectableCardGroup
        multiple={section.multiple}
        options={section.options}
        value={value as never}
        onChange={(next: string | string[]) => {
          if (section.multiple && section.max && Array.isArray(next) && next.length > section.max) {
            return;
          }
          onChange(next);
        }}
        variant={section.variant ?? 'icon'}
      />
    </div>
  );
}

function TextareaSectionRenderer({
  section,
  value,
  onChange,
}: {
  section: TextareaSection;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <SectionTitle title={section.title} desc={section.desc} />
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={section.placeholder}
        rows={section.rows ?? 5}
        className="w-full rounded-2xl bg-white border border-cream-dark px-4 py-3 text-sm text-navy placeholder:text-beige-dark focus:border-navy focus:outline-none transition-colors resize-none"
      />
    </div>
  );
}

function KnownShopSectionRenderer({
  section,
  value,
  onChange,
}: {
  section: KnownShopSection;
  value: KnownShopValue;
  onChange: (value: KnownShopValue) => void;
}) {
  const [searchWord, setSearchWord] = useState('');
  const max = section.max ?? 3;

  const setNoKnownShop = useCallback(() => {
    onChange({ hasKnownShop: false, shopNames: [] });
  }, [onChange]);

  const setHasKnownShop = useCallback(() => {
    onChange({ hasKnownShop: true, shopNames: value.shopNames });
  }, [onChange, value.shopNames]);

  const addKnownShop = useCallback(
    (shopName: string) => {
      const trimmed = shopName.trim();
      if (!trimmed) return;

      if (value.shopNames.includes(trimmed)) {
        onChange({ ...value, hasKnownShop: true });
        setSearchWord('');
        return;
      }

      if (value.shopNames.length >= max) return;

      onChange({
        hasKnownShop: true,
        shopNames: [...value.shopNames, trimmed],
      });
      setSearchWord('');
    },
    [max, onChange, value]
  );

  const toggleShop = useCallback(
    (shopName: string) => {
      const exists = value.shopNames.includes(shopName);

      if (exists) {
        onChange({
          hasKnownShop: true,
          shopNames: value.shopNames.filter((name) => name !== shopName),
        });
        return;
      }

      if (value.shopNames.length >= max) return;

      onChange({
        hasKnownShop: true,
        shopNames: [...value.shopNames, shopName],
      });
    },
    [max, onChange, value]
  );

  return (
    <div className="space-y-4">
      <SelectableCard
        option={{
          key: 'unknown-shop',
          title: '아니요, 아직 정해지지 않았어요.',
          desc: '고객님께 맞는 예복샵을 추천해드릴게요.',
          icon: 'ri-compass-3-line',
        }}
        active={value.hasKnownShop === false}
        onClick={setNoKnownShop}
        variant="icon"
      />

      <SelectableCard
        option={{
          key: 'known-shop',
          title: '네, 생각해둔 예복샵이 있어요.',
          desc: '방문을 원하는 예복샵을 검색하여 선택해 주세요.',
          icon: 'ri-search-eye-line',
        }}
        active={value.hasKnownShop === true}
        onClick={setHasKnownShop}
        variant="icon"
      />

      {value.hasKnownShop === true && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="rounded-2xl bg-white border border-cream-dark p-5 space-y-4"
        >
          <SearchInput
            value={searchWord}
            onChange={setSearchWord}
            onSubmit={addKnownShop}
            placeholder="예복샵 이름을 검색하세요."
            submitLabel="추가"
          />

          <div>
            <p className="text-[11px] font-medium text-navy-light mb-2">자주 선택되는 예복샵</p>
            <ChipGroup items={section.candidates} value={null} onSelect={toggleShop} />
          </div>

          {value.shopNames.length > 0 && (
            <div>
              <p className="text-[11px] font-medium text-navy-light mb-2">
                선택한 예복샵 {value.shopNames.length}/{max}
              </p>
              <div className="flex flex-wrap gap-2">
                {value.shopNames.map((shopName) => (
                  <button
                    key={shopName}
                    type="button"
                    onClick={() => toggleShop(shopName)}
                    className="inline-flex items-center gap-1 rounded-full bg-navy text-white px-3 py-1.5 text-xs cursor-pointer"
                  >
                    {shopName}
                    <i className="ri-close-line text-xs" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}

function DateSectionRenderer({
  section,
  value,
  onChange,
}: {
  section: DateSection;
  value: string | null;
  onChange: (value: string | null) => void;
}) {
  return (
    <div className="space-y-4">
      <DatePreferencePicker value={value} onChange={onChange} quickOptions={section.quickOptions} />

      {section.notice && (
        <InfoNotice title={section.notice.title} icon={section.notice.icon}>
          <p>{section.notice.content}</p>
        </InfoNotice>
      )}

      {section.undecidedOption && (
        <SelectableCard
          option={section.undecidedOption}
          active={value === 'undecided'}
          onClick={() => onChange('undecided')}
          variant="icon"
        />
      )}

      {hasDateFormat(value) && (
        <p className="text-[11px] text-navy-light text-center">
          선택한 날짜 기준으로 상담 가능한 일정을 확인할게요.
        </p>
      )}
    </div>
  );
}

export default function FormSectionRenderer({
  section,
  values,
  setFieldValue,
}: FormSectionRendererProps) {
  if (section.type === 'counter') {
    return (
      <CounterList
        items={section.items}
        value={values[section.key]}
        onChange={(next) => setFieldValue(section.key, next)}
        totalLabel={section.totalLabel}
      />
    );
  }

  if (section.type === 'select') {
    return (
      <SelectSectionRenderer
        section={section}
        value={values[section.key] as string | string[] | null}
        onChange={(next) => setFieldValue(section.key, next as never)}
      />
    );
  }

  if (section.type === 'textarea') {
    return (
      <TextareaSectionRenderer
        section={section}
        value={(values[section.key] as string) ?? ''}
        onChange={(next) => setFieldValue(section.key, next as never)}
      />
    );
  }

  if (section.type === 'knownShop') {
    return (
      <KnownShopSectionRenderer
        section={section}
        value={values.knownShop}
        onChange={(next) => setFieldValue('knownShop', next)}
      />
    );
  }

  if (section.type === 'date') {
    return (
      <DateSectionRenderer
        section={section}
        value={values.visitDate}
        onChange={(next) => setFieldValue('visitDate', next)}
      />
    );
  }

  if (section.type === 'notice') {
    return (
      <InfoNotice title={section.title} icon={section.icon}>
        <p>{section.content}</p>
      </InfoNotice>
    );
  }

  return null;
}